import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import {
  ClientStore,
  Order,
  LocationValid,
  OrderItem,
  Availability,
  StoreRelations,
} from 'src/app/stores/stores';
import { Store } from '@ngrx/store';
import { Cart, OrderMetaState, ZoneState } from '../+state/stores.reducer';
import {
  getCurrentCartUuid,
  getCurrentCartState,
  getStoreLocationsState,
  getCurrentOrderMetaState,
  getCheckoutState,
  getSelectedStore,
  getZoneState,
  getStoreRules,
  getSelectedStoreOpeningHours,
} from '../+state/stores.selectors';
import { LocationService } from '../../location.service';
import {
  AddCheckoutState,
  AddRuleOrderItem,
  AddOrderMeta,
  ErrorMessage,
  ClearStoreLocation,
  UpdateLocation,
  ProceedToCheckout,
} from '../+state/stores.actions';
import { FormatPrice } from 'src/app/shared/format-price.pipe';
import {
  DELIVERY_METHODS,
  DELIVERY_METHOD_VALUES,
} from '../types/DeliveryMethod';
import { SpecialSchedule } from 'src/app/stores/store-schedule/stores-schedule';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';
import { Event } from 'src/app/stores/event';
import { environment } from 'src/environments/environment';
import { AbstractControl } from '@angular/forms';
import { OrderingSetting } from 'src/app/stores/+state/stores.reducer';
import { CustomerAuthService } from '../../customer-auth.service';
import { HelperService } from '../../helper.service';
import { Router } from '@angular/router';
import StoreUtils from '../utils/StoreUtils';
import { PaymentMethod } from '../types/PaymentMethod';
import { CustomerCheckoutRecommendationsFlowCoordinator } from '../store-checkout-recommendations/customer-checkout-recommendations-flow.coordinator';
import {
  hasCompletePickupAndDropoffAddressState,
  isPickupAndDropoffFulfilmentMethod,
} from '../utils/associated-zone-lookup.helper';

export const PICKUP_METHOD = {
  UNDEFINED: -1,
  AT_LOCATION: 0,
  MY_SELF: 1,
  AT_ADDRESS: 2,
};
export const PAYMENT_OPTION = {
  UNDEFINED: -1,
  PAY_LATER: 0,
  PAY_NOW: 1,
};

export const EMAIL_STATUS = {
  UNDEFINED: -1,
  FAILED: 0,
  SUCCESS: 1,
  SENDING: 2,
};

export const ORIGIN_TYPE = {
  APP: 'APP',
  WEB: 'WEB',
};

export interface DeliveryFeeDisplayState {
  showDeliveryZoneNameNextToFee: boolean;
  deliveryFeeStoreZoneName: string;
  showAddressDeliveryFeeHint: boolean;
  showDeliveryFeeRow: boolean;
}

const INITIAL_DELIVERY_FEE_DISPLAY_STATE: DeliveryFeeDisplayState = {
  showDeliveryZoneNameNextToFee: false,
  deliveryFeeStoreZoneName: '',
  showAddressDeliveryFeeHint: false,
  showDeliveryFeeRow: false,
};

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  storeRules;
  selectedStore: ClientStore;
  selectedStoreLocale: string;
  selectedStoreCurrency: string;
  selectedStoreCurrencySymbol: string;
  cartData: Order;
  cartStatus: string;
  cartItems: OrderItem[];
  storeLocation: string | number;
  orderMetaData: OrderMetaState;
  orderUuid: string;
  selectedPickupMethod: number;
  selectedPaymentOption: number; // id of payment method: -1 - undefined; 0 - pay later; 1 - pay now
  selectedDigitalPaymentMethod: string;
  validLocations: LocationValid = null;
  private checkoutStateData;
  private checkoutStatus: string;
  private OrderBtnDisabled: boolean;
  addToCartDisabled: boolean;
  zoneStateData: ZoneState;
  private applicableStoreRules = [];
  private applicableFeeRules = [];
  private storeOpeningHours: SpecialSchedule = null;

  checkoutReady = false;
  showDeliveryZoneNameNextToFee = false;
  deliveryFeeStoreZoneName = '';
  showAddressDeliveryFeeHint = false;
  showDeliveryFeeRow = false;
  private readonly deliveryFeeDisplayStateSubject =
    new BehaviorSubject<DeliveryFeeDisplayState>(
      INITIAL_DELIVERY_FEE_DISPLAY_STATE,
    );
  readonly deliveryFeeDisplayState$ =
    this.deliveryFeeDisplayStateSubject.asObservable();

  constructor(
    private store: Store<Cart>,
    private locationService: LocationService,
    private formatPricePipe: FormatPrice,
    private customerAuthService: CustomerAuthService,
    private helper: HelperService,
    private router: Router,
    private recommendationsFlowCoordinator: CustomerCheckoutRecommendationsFlowCoordinator,
  ) {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    dayjs.extend(isBetween);
    this.addToCartDisabled = true;

    this.store.select(getSelectedStoreOpeningHours).subscribe((value) => {
      this.storeOpeningHours = value;
    });
    this.store.select(getStoreRules).subscribe((value) => {
      if (value && value.storeRulesState && value.storeRulesState.storeRules) {
        this.storeRules = value.storeRulesState.storeRules;
      }
    });

    combineLatest([
      this.store.select(getSelectedStore),
      this.store.select(getCurrentCartUuid),
      this.store.select(getCurrentCartState),
      this.store.select(getStoreLocationsState),
      this.store.select(getCurrentOrderMetaState),
    ]).subscribe((state) => {
      if (state && state[0]) {
        this.selectedStore = state[0];
        if (
          this.selectedStore &&
          this.selectedStore.address &&
          this.selectedStore.currency
        ) {
          this.selectedStoreLocale = StoreUtils.getStoreLocale(
            this.selectedStore,
          );
          this.selectedStoreCurrency = StoreUtils.getCurrencyIsoCode(
            this.selectedStore,
          );
          this.selectedStoreCurrencySymbol = StoreUtils.getCurrencySymbol(
            this.selectedStore,
          );
        }
      }
      if (state && state[1]) {
        this.orderUuid = state[1];
      }
      if (state && state[2]) {
        this.cartData = state[2];
        this.cartItems = this.cartData.orderItems;
      }
      if (state && this.selectedStore && this.orderUuid && state[3]) {
        this.validLocations = state[3];
      }
      if (state[4]) {
        this.orderMetaData = state[4];
        if (
          this.orderMetaData.data.paymentOption &&
          this.cartData.totalDiscountedPrice == 0
        ) {
          this.setOrderMetaState('paymentOption', undefined, {
            src: 'CheckoutService',
            description: 'Payment option changed',
          });
        }
      }
      this.updateDeliveryFeeDisplayState();
    });

    // get checkout state
    this.store.select(getCheckoutState).subscribe((d) => {
      if (d?.checkoutState) {
        this.checkoutStatus = d.checkoutState.status;
        this.checkoutStateData = d.checkoutState.data;

        if (this.checkoutStateData) {
          this.OrderBtnDisabled = this.checkoutStateData.orderBtnDisabled;
        }
      }
    });

    // get zone state
    this.store.select(getZoneState).subscribe((d) => {
      if (d?.zoneState) {
        this.zoneStateData = d.zoneState;
      }
    });
  }

  isNotDeliverableAddressByZone(): boolean {
    return (
      this.getZoneRestrictions() &&
      this.zoneStateData.status === 'NOTDELIVERABLE_POSTCODE' &&
      this.isDeliveryAddressGpsCoordinatesEnabled()
    );
  }

  isNotDeliverableAddressByGeocode(): boolean {
    return (
      this.getZoneRestrictions() &&
      this.zoneStateData.status === 'NOTDELIVERABLE_ADDRESS'
    );
  }
  getTransactionFees() {
    return this.cartData.transactionFee;
  }

  getTransactionTip() {
    return this.cartData.tip;
  }

  /**
   * Utility: Compare two wishTime values using dayjs
   */
  private isWishTimeDifferent(
    oldVal: string | number,
    newVal: string | number,
  ): boolean {
    const oldDayjs = oldVal ? dayjs(oldVal) : null;
    const newDayjs = newVal ? dayjs(newVal) : null;
    if (oldDayjs && newDayjs && oldDayjs.isValid() && newDayjs.isValid()) {
      return !oldDayjs.isSame(newDayjs);
    }
    return oldVal !== newVal;
  }

  /**
   * TODO: setters should NOT be publically available
   */
  setOrderMetaState(k: string, v: string | number, e?: Event) {
    // Only dispatch if the value for the key is different
    if (
      Object.prototype.hasOwnProperty.call(this.orderMetaData?.data ?? {}, k)
    ) {
      if (k === 'wishTime') {
        if (this.isWishTimeDifferent(this.orderMetaData.data[k], v)) {
          this.store.dispatch(new AddOrderMeta(k, v, e));
        }
      } else {
        if (this.orderMetaData.data[k] !== v) {
          this.store.dispatch(new AddOrderMeta(k, v, e));
        }
      }
    } else {
      this.store.dispatch(new AddOrderMeta(k, v, e));
    }
  }

  setCheckoutState(k: string, v: string | boolean | number, e?: Event) {
    this.store.dispatch(new AddCheckoutState(k, v, e));
  }

  getSelectedDigitalPaymentMethod() {
    return this.selectedDigitalPaymentMethod;
  }

  setSelectedDigitalPaymentMethod(selectedDigitalPaymentMethod: string) {
    this.selectedDigitalPaymentMethod = selectedDigitalPaymentMethod;
  }

  setApplicableStoreRulesToInit() {
    this.applicableStoreRules = [];
  }

  setApplicableFeeRulesToInit() {
    this.applicableFeeRules = [];
  }

  setApplicableStoreRule(rule) {
    this.applicableStoreRules.push(rule);
  }

  setApplicableFeeRule(rule) {
    this.applicableFeeRules.push(rule);
  }

  /**
   * Total is hidden only when all items are 0 AND total is 0
   */
  shouldDisplayTotal() {
    if (!!this.cartItems) {
      for (const item of this.cartItems) {
        if (item.totalNonDiscountedPrice !== 0) {
          return true;
        }
      }
      const total = this.getCartTotal();
      return total === 0 ? false : true;
    } else {
      return false;
    }
  }

  /**
   * GETTERS
   */
  getCartTotal(itemsOnly = false, includeRules = false) {
    let ret = 0;
    if (this.cartItems) {
      this.cartItems.forEach((item) => {
        if (
          (includeRules && item.hierarchyLevel !== 'RULE_DELIVERY_FEE') ||
          (!includeRules && !this.isOrderingRuleItem(item))
        ) {
          ret += item.discountType
            ? item.totalDiscountedPrice
            : item.totalNonDiscountedPrice;
        }
      });
      if (!itemsOnly) {
        if (
          this.selectedStore &&
          ((this.getOrderFeeDelivery() && this.ifOnlyDeliveryToAddress()) ||
            (this.getOrderFeeDelivery() && this.getPickupMethodAsInt() === 2))
        ) {
          ret += this.getOrderFeeDelivery();
        }
        if (
          this.cartData.transactionFee &&
          !this.locationService.isAdminOrderCaptureUpdate()
        ) {
          ret += this.cartData.transactionFee;
        }
        if (this.cartData.voucherDiscount) {
          ret = ret - this.cartData.voucherDiscount;
        }
      }
    }
    return ret;
  }

  getCartTotalWithoutVoucher(includeRules = false, includeTips = true) {
    let ret = 0;
    if (this.cartItems) {
      this.cartItems.forEach((item) => {
        if (
          (includeRules && item.hierarchyLevel !== 'RULE_DELIVERY_FEE') ||
          (!includeRules && !this.isOrderingRuleItem(item))
        ) {
          const qty = item.quantity;
          ret += item.discountType
            ? item.totalDiscountedPrice
            : item.totalNonDiscountedPrice;
        }
      });
      if (this.selectedStore && this.getOrderFeeDelivery()) {
        ret += this.getOrderFeeDelivery();
      }
      if (this.getOrderServiceCharge()) {
        ret += this.getOrderServiceCharge();
      }
      if (
        this.getTransactionFees() &&
        !this.locationService.isAdminOrderCaptureUpdate()
      ) {
        ret += this.getTransactionFees();
      }
      if (this.getTransactionTip() && includeTips) {
        ret += this.getTransactionTip();
      }
    }
    return ret;
  }

  getCartDiscountedPrice() {
    let ret = 0;
    if (this.cartData && this.cartData.voucherDiscount) {
      ret = this.cartData.totalDiscountedPrice;
    }
    return ret;
  }

  getCartItemsSubtotal() {
    let ret = 0;
    if (
      this.cartData &&
      this.cartData.itemsSubtotalPrice != this.cartData.totalDiscountedPrice
    ) {
      ret = this.cartData.itemsSubtotalPrice;
    }
    return ret;
  }

  getCartDiscountType() {
    if (this.cartData && this.cartData.voucherDiscount) {
      return this.cartData.voucherDiscountType;
    }
    return null;
  }

  getCartDiscountValue() {
    if (this.cartData && this.cartData.voucherDiscount) {
      return this.cartData.voucherDiscount;
    }
    return null;
  }

  // This function is only called when voucher discount type is percentile...
  getCartDiscountPercentage() {
    if (this.cartData && this.cartData.voucherDiscountPercentage) {
      return this.cartData.voucherDiscountPercentage;
    }
    return null;
  }

  getFormattedMinAmount() {
    if (this.selectedStore) {
      return this.formatPricePipe.transform(
        this.getOrderMinAmountDelivery(),
        this.selectedStoreLocale,
        this.selectedStoreCurrency,
        this.selectedStoreCurrencySymbol,
      );
    }
    return '';
  }
  getLocationInitiallyPersisted() {
    return this.checkoutStateData.locationInitiallyPersisted;
  }
  getOrderMetaData(k: string) {
    if (this.orderMetaData && this.orderMetaData.data[k]) {
      return this.orderMetaData.data[k];
    }
    return '';
  }
  getCheckoutMetaData(k: string) {
    if (this.checkoutStateData && this.checkoutStateData[k]) {
      return this.checkoutStateData[k];
    }
    return '';
  }
  getCheckoutMetaDataValue(k: string) {
    if (this.checkoutStateData && k in this.checkoutStateData) {
      return this.checkoutStateData[k];
    }
    return '';
  }
  /**
   * check if CheckoutMetaData key is set to any value
   * @param k CheckoutMetaData key
   */
  isCheckoutMetaData(k: string) {
    if (this.checkoutStateData && k in this.checkoutStateData) {
      return true;
    }
    return false;
  }

  // payment getters
  getPickupMethod() {
    return this.orderMetaData.data.deliveryMethod;
  }
  getPickupMethodStr() {
    if (this.orderMetaData?.data.deliveryMethod) {
      return DELIVERY_METHODS[this.orderMetaData.data.deliveryMethod];
    } else {
      return null;
    }
  }
  getPickupMethodAsInt() {
    return parseInt(this.orderMetaData.data.deliveryMethod, 10);
  }

  deliveryFeeApply() {
    return this.cartData?.deliveryFeeApply;
  }

  locationDeliveryFeeApply() {
    return this.validLocations?.deliveryFeeApply;
  }

  getPickupMethodAsIntFrom(deliveryMethod: string) {
    return parseInt(DELIVERY_METHOD_VALUES[deliveryMethod], 10);
  }
  getStoreLocation() {
    return this.checkoutStateData.storeLocation;
  }

  getPaymentOptions() {
    return this.orderMetaData.data.paymentOption;
  }

  getApplicableStoreRules() {
    return this.applicableStoreRules;
  }

  getApplicableFeeRules() {
    return this.applicableFeeRules;
  }

  getOrderSubmitErrors() {
    return this.checkoutStateData.orderSubmitError.errors;
  }

  // EOF: getters

  validCartLocation() {
    if (this.cartData.location && this.cartData.location !== -1) {
      return true;
    }
    return false;
  }
  validateCartLocation() {
    if (!this.validLocations && this.cartData.location && !this.orderMetaData) {
      return true;
    }
    return false;
  }
  getValidLocations() {
    return this.validLocations;
  }
  isValidLocation() {
    // Previous-order lookup: the FK on the order points at the previous order's
    // location row (cross-store) and is authorized by the backend validator
    // (ValidDeliveryLocationValidator) via OrderProperty.PREVIOUS_ORDER_ID.
    // Frontend has no `validLocations` entry for it (we don't run
    // ValidateStoreLocations against the current store), so don't gate the
    // submit button on a validation result we deliberately skipped.
    if (this.hasPreviousOrderLookupLocation()) {
      return true;
    }
    return this.validLocations && this.validLocations.isValid;
  }

  clearValidLocations() {
    this.validLocations = null;
  }

  isSettingsEnabled() {
    return 'yes';
  }

  isShowReadOnlyLocation() {
    if (
      this.ifOnlyInStore() ||
      this.showReadOnlyLocation() ||
      this.showEditableLocation() ||
      this.getPickupMethodAsInt() === PICKUP_METHOD.AT_LOCATION
    ) {
      return true;
    }
    return false;
  }

  isShowOnlySelfPickup() {
    if (
      this.ifOnlySelfPickup() ||
      (this.ifEnabledSelfPickup() &&
        !this.showReadOnlyLocation() &&
        this.getPickupMethodAsInt() === PICKUP_METHOD.MY_SELF)
    ) {
      return true;
    }
    return false;
  }

  isShowOnlyDeliveryToAddress() {
    if (
      this.ifOnlyDeliveryToAddress() ||
      (this.ifEnabledDeliveryAtAddress() &&
        this.getPickupMethodAsInt() === PICKUP_METHOD.AT_ADDRESS)
    ) {
      return true;
    }
    return false;
  }

  showReadOnlyLocation() {
    if (
      this.ifEnabledInStorePickup() &&
      !this.ifOnlySelfPickup() &&
      !this.ifOnlyDeliveryToAddress() &&
      this.hasPreviousOrderLookupLocation()
    ) {
      return true;
    }
    if (
      this.ifEnabledInStorePickup() &&
      !this.ifOnlySelfPickup() &&
      !this.ifOnlyDeliveryToAddress() &&
      this.locationService.getUrlStoreLocation() &&
      this.getValidLocations() &&
      this.getValidLocations().label ===
        this.locationService.getUrlStoreLocation() &&
      this.locationService.getLocationType() != 'cart-location'
    ) {
      return true;
    }
    return false;
  }

  showEditableLocation() {
    if (this.hasPreviousOrderLookupLocation()) {
      return false;
    }
    if (
      (this.ifEnabledInStorePickup() &&
        this.getPickupMethodAsInt() === 0 &&
        this.showChoices()) ||
      (this.ifOnlyInStore() &&
        (!this.locationService.getUrlStoreLocation() ||
          (this.locationService.getUrlStoreLocation() &&
            this.getValidLocations() &&
            this.getValidLocations().label !==
              this.locationService.getUrlStoreLocation()) ||
          this.locationService.getLocationType() == 'cart-location'))
    ) {
      return true;
    }
    return false;
  }

  showChoices() {
    if (
      this.ifOnlyInStore() ||
      this.ifOnlySelfPickup() ||
      this.ifOnlyDeliveryToAddress()
    ) {
      return false;
    }
    if (!this.showReadOnlyLocation() && this.ifEnabledMultiplePickupOptions()) {
      return true;
    }
    return false;
  }

  private hasPreviousOrderLookupLocation(): boolean {
    const orderMeta = this.orderMetaData?.data;
    return (
      !!orderMeta?.PREVIOUS_ORDER_ID &&
      orderMeta.location !== undefined &&
      orderMeta.location !== null &&
      orderMeta.location !== ''
    );
  }

  // atm we are actually not showing any delivery options if only self pickup is available
  showOnlySelfPickup() {
    if (
      !this.getStoreLocation() ||
      !this.getLocationInitiallyPersisted() ||
      (!!this.cartData.location && this.ifOnlySelfPickup())
    ) {
      return true;
    }
    return false;
  }

  // atmm we are actually not showing any delivery options if only delivery to address is available
  showOnlyDeliveryToAddress() {
    if (
      !this.getStoreLocation() ||
      !this.getLocationInitiallyPersisted() ||
      (!!this.cartData.location && this.ifOnlyDeliveryToAddress())
    ) {
      return true;
    }
    return false;
  }

  storeAvailabilitiesInDay(day: string) {
    // If daysOfWeek is null we assume the schedule applies to all days in week
    return this.storeOpeningHours?.schedule?.availabilities?.filter(
      (a) =>
        a.type === 'DAYS_OF_WEEK' &&
        (!a.daysOfWeek || a.daysOfWeek.includes(day)),
    );
  }

  ifStoreClosed() {
    return !this.isStoreOpen();
  }

  isClosedForOrdering() {
    return this.ifStoreClosed() && !this.isFutureOrderingEnabled();
  }

  isStoreOpen() {
    if (!this.storeOpeningHours) {
      return true;
    }

    const dateSchedule = this.storeOpeningHours.schedule.availabilities
      .filter((a) => a.date != null)
      .find(
        (a) =>
          this.currentStoreTime.isSame(a.date, 'day') &&
          this.currentStoreTime.isBetween(
            this.storeOpeningTime(dayjs(), a),
            this.storeClosingTime(dayjs(), a),
            'minute',
            '[]',
          ),
      );

    if (dateSchedule) {
      if (dateSchedule.type === 'DATE_INCLUSIVE') {
        return dateSchedule;
      }
      if (dateSchedule.type === 'DATE_EXCLUSIVE') {
        return null;
      }
    }

    const storeSchedulesForDay = this.storeAvailabilitiesInDay(
      this.currentStoreTime.format('ddd').toUpperCase(),
    );
    const openNow = storeSchedulesForDay.find((s) =>
      this.currentStoreTime.isBetween(
        this.storeOpeningTime(dayjs(), s),
        this.storeClosingTime(dayjs(), s),
        'minute',
        '[]',
      ),
    );

    return openNow;
  }

  /**
   * Return current time in store's timezone
   */
  private get currentStoreTime() {
    return dayjs();
  }

  private storeOpeningTime(date: dayjs.Dayjs, availability: Availability) {
    const openingTime = this.storeAvailabilityStart(availability);
    const openingDate = date
      .hour(openingTime.hour)
      .minute(openingTime.minute)
      .second(openingTime.second);
    return this.toStoreTimezone(openingDate);
  }

  private storeClosingTime(date: dayjs.Dayjs, availability: Availability) {
    const closingTime = this.storeAvailabilityEnd(availability);
    const closingDate = date
      .hour(closingTime.hour)
      .minute(closingTime.minute)
      .second(closingTime.second);
    return this.toStoreTimezone(closingDate);
  }

  private toStoreTimezone(date: dayjs.Dayjs) {
    return this.selectedStore.timeZone
      ? date.tz(this.selectedStore.timeZone, true)
      : date;
  }

  private storeAvailabilityStart(availability: Availability) {
    const startTime = [...availability.startTime.split(':')];
    return {
      hour: +startTime[0],
      minute: +startTime[1],
      second: +startTime[2],
    };
  }

  private storeAvailabilityEnd(availability: Availability) {
    const endTime = [...availability.endTime.split(':')];
    return { hour: +endTime[0], minute: +endTime[1], second: +endTime[2] };
  }

  ifEnabledOrder() {
    if (
      !this.selectedStore ||
      !this.selectedStore.subscription ||
      this.selectedStore.subscription.status === 'TRIAL_EXCEEDED'
    ) {
      return false;
    }

    if (
      this.selectedStore &&
      this.selectedStore.settings &&
      this.selectedStore.settings.ENABLE_ORDERING
    ) {
      return this.selectedStore.settings.ENABLE_ORDERING;
    }

    return false;
  }

  ifEnabledOrderCapture() {
    if (
      this.selectedStore &&
      this.selectedStore.settings &&
      this.selectedStore.settings.ENABLE_ORDER_CAPTURE
    ) {
      return this.selectedStore.settings.ENABLE_ORDER_CAPTURE;
    }
    return false;
  }

  ifNoDeliverySupported() {
    if (
      this.selectedStore &&
      this.selectedStore.settings &&
      !this.selectedStore.settings.DELIVERY_IN_STORE_LOCATION &&
      !this.selectedStore.settings.DELIVERY_NO_LOCATION &&
      !this.selectedStore.settings.DELIVERY_ADDRESS
    ) {
      return true;
    }
    return false;
  }
  ifNoDeliveryAvailable() {
    if (
      this.selectedStore &&
      this.selectedStore.settings &&
      !this.ifEnabledInStorePickup() &&
      !this.ifEnabledSelfPickup() &&
      !this.ifEnabledDeliveryAtAddress()
    ) {
      return true;
    }
    return false;
  }

  ifOnlySelfPickup() {
    if (
      this.selectedStore &&
      this.selectedStore.settings &&
      !this.ifEnabledInStorePickup() &&
      this.ifEnabledSelfPickup() &&
      !this.ifEnabledDeliveryAtAddress()
    ) {
      return true;
    }
    return false;
  }

  ifOnlyInStore() {
    if (
      this.selectedStore &&
      this.selectedStore.settings &&
      this.ifEnabledInStorePickup() &&
      !this.ifEnabledSelfPickup() &&
      !this.ifEnabledDeliveryAtAddress()
    ) {
      return true;
    }
    return false;
  }

  ifOnlyDeliveryToAddress() {
    if (
      this.selectedStore &&
      this.selectedStore.settings &&
      !this.ifEnabledInStorePickup() &&
      !this.ifEnabledSelfPickup() &&
      this.ifEnabledDeliveryAtAddress()
    ) {
      return true;
    }
    return false;
  }

  ifOnlySelfPickupForMultiStore() {
    if (
      this.selectedStore &&
      this.selectedStore.settings &&
      this.ifEnabledSelfPickupMultiStore() &&
      !this.ifEnabledDeliveryAtAddressMultiStore()
    ) {
      return true;
    }
    return false;
  }

  ifEnabledMultiplePickupOptions() {
    if (this.selectedStore && this.selectedStore.settings) {
      if (
        this.helperAtLeastTwo(
          this.selectedStore.settings.DELIVERY_IN_STORE_LOCATION,
          this.selectedStore.settings.DELIVERY_NO_LOCATION,
          this.selectedStore.settings.DELIVERY_ADDRESS,
        )
      ) {
        return true;
      }
    }
    return false;
  }
  helperAtLeastTwo(a: boolean, b: boolean, c: boolean) {
    return a ? b || c : b && c;
  }

  focusField(event) {
    const closestInput = this.closestInput(event.target);
    if (closestInput) {
      closestInput.focus();
    }
  }

  closestInput(target) {
    const expectedTypes = ['TEXTAREA', 'INPUT'];
    if (!target) {
      return null;
    }
    if (target && expectedTypes.includes(target.nodeName)) {
      return target;
    }
    if (!target.previousSibling && target.parentNode) {
      return this.closestInput(target.parentNode);
    }
    return this.closestInput(target.previousSibling);
  }

  /**
   * check if payment is enabled
   */
  ifEnabledPayment() {
    if (
      (this.selectedStore &&
        this.selectedStore.settings &&
        this.selectedStore.settings.PAYMENT_OPTION === 'disabled') ||
      this.countEnabledPaymentMethods() === 0
    ) {
      return false;
    }
    return true;
  }

  /**
   * check if payment is enabled and optional
   */
  ifEnabledPaymentSelection() {
    if (
      this.selectedStore &&
      this.selectedStore.settings &&
      this.selectedStore.settings.PAYMENT_OPTION === 'optional' &&
      this.countEnabledPaymentMethods() !== 0
    ) {
      return true;
    }
    return false;
  }

  /**
   * check if payment is enabled and mandatory
   */
  ifEnabledPaymentMandatory() {
    if (
      this.selectedStore &&
      this.selectedStore.settings &&
      this.selectedStore.settings.PAYMENT_OPTION === 'mandatory' &&
      this.countEnabledPaymentMethods() !== 0
    ) {
      // set payment option to pay now
      // this.setPaymentOption(1);
      return true;
    }
    return false;
  }

  /**
   * check if payment is enabled, optional & preselected
   */
  ifEnabledPaymentSelectionPreselected() {
    return (
      this.ifEnabledPaymentSelection() &&
      this.selectedStore.settings.OPTIONAL_PAYMENT_PRESELECTED
    );
  }

  ifInactivePaymentSelection() {
    return this.selectedStore.settings.PAYMENT_OPTION === 'disabled';
  }

  /**
   * check if payment is enabled, mandatory & preselected
   */
  ifEnabledPaymentMandatoryPreselected() {
    return (
      this.ifEnabledPaymentMandatory() &&
      this.selectedStore.settings.MANDATORY_PAYMENT_PRESELECTED
    );
  }

  /**
   * count available payment methods
   */
  countEnabledPaymentMethods() {
    if (!this.selectedStore || !this.selectedStore.settings) {
      return 0;
    }
    return this.selectedStore?.activePaymentMethods?.length;
  }
  /**
   * If delivery-in-store-location = false AND delivery-no-location = true
   * location pickup is disabled
   */
  ifEnabledInStorePickup() {
    if (
      !this.selectedStore ||
      !this.selectedStore.settings ||
      !this.selectedStore.settings.DELIVERY_IN_STORE_LOCATION
    ) {
      return false;
    }
    if (
      this.ifStoreClosed() &&
      !this.selectedStore.settings
        .DELIVERY_IN_STORE_LOCATION_CHOOSE_FUTURE_ORDER_DATE
    ) {
      return false;
    }
    return true;
  }
  ifEnabledSelfPickup() {
    if (
      !this.selectedStore ||
      !this.selectedStore.settings ||
      !this.selectedStore.settings.DELIVERY_NO_LOCATION
    ) {
      return false;
    }
    if (
      this.ifStoreClosed() &&
      !this.selectedStore.settings.DELIVERY_NO_LOCATION_CHOOSE_FUTURE_ORDER_DATE
    ) {
      return false;
    }
    return true;
  }

  ifEnabledDeliveryAtAddress() {
    if (
      !this.selectedStore ||
      !this.selectedStore.settings ||
      !this.selectedStore.settings.DELIVERY_ADDRESS
    ) {
      return false;
    }
    if (
      this.ifStoreClosed() &&
      !this.selectedStore.settings.DELIVERY_ADDRESS_CHOOSE_FUTURE_ORDER_DATE
    ) {
      return false;
    }
    return true;
  }

  ifEnabledSelfPickupMultiStore() {
    return this.getAllChildStores().some((childStore) => {
      if (!childStore.settings || !childStore.settings.DELIVERY_NO_LOCATION) {
        return false;
      }
      return true;
    });
  }

  ifEnabledDeliveryAtAddressMultiStore() {
    return this.getAllChildStores().some(
      (childStore) => childStore.settings?.DELIVERY_ADDRESS,
    );
  }

  ifDeliveryMethodEnabled(deliveryMethod: string) {
    if (this.selectedStore && this.selectedStore.settings) {
      switch (deliveryMethod) {
        case 'IN_STORE_LOCATION':
          return this.ifEnabledInStorePickup();
        case 'NO_LOCATION':
          return this.ifEnabledSelfPickup();
        case 'ADDRESS':
          return this.ifEnabledDeliveryAtAddress();
      }
    }
    return false;
  }

  ifCollapsableDisabled() {
    if (
      this.showChoices() &&
      (isNaN(this.getPickupMethodAsInt()) || this.getPickupMethodAsInt() === -1)
    ) {
      return true;
    }
    return false;
  }

  ifMinimumOrderAmountMet(type = '') {
    if (!this.selectedStore || !this.cartItems || this.cartItems.length === 0) {
      return true;
    }
    // No minimum of either kind configured -> nothing to enforce. Both zero is the disabled
    // state (there is no separate on/off toggle for either minimum).
    if (
      this.getOrderMinAmountDelivery() === 0 &&
      this.getEffectiveLocationMinimum() === 0
    ) {
      return true;
    }
    const cartTotal = this.getCartTotal(true, true);
    switch (type) {
      case 'onlyDelivery':
        if (
          this.ifOnlyDeliveryToAddress() &&
          this.getOrderMinAmountDelivery() > cartTotal
        ) {
          return false;
        }
        break;
      case 'deliveryAsOption':
        if (
          this.showChoices() &&
          this.ifEnabledDeliveryAtAddress() &&
          this.getOrderMinAmountDelivery() > cartTotal
        ) {
          return false;
        }
        break;
      case 'onlyLocation':
        if (
          this.showReadOnlyLocation() &&
          this.getEffectiveLocationMinimum() > cartTotal
        ) {
          return false;
        }
        break;
      case 'locationAsOption':
        if (
          this.showChoices() &&
          this.getEffectiveLocationMinimum() > cartTotal
        ) {
          return false;
        }
        break;
      default:
        const isDeliveryToAddress =
          this.ifOnlyDeliveryToAddress() ||
          (this.showChoices() &&
            this.ifEnabledDeliveryAtAddress() &&
            this.getPickupMethodAsInt() === PICKUP_METHOD.AT_ADDRESS);

        const isAtLocation =
          this.getPickupMethodAsInt() === PICKUP_METHOD.AT_LOCATION;

        if (
          isDeliveryToAddress &&
          this.getOrderMinAmountDelivery() > cartTotal
        ) {
          return false;
        }
        if (isAtLocation && this.getEffectiveLocationMinimum() > cartTotal) {
          return false;
        }
        break;
    }
    return true;
  }

  getZoneRestrictions(): boolean {
    return !!this.selectedStore.settings.DELIVERY_ZONE_RESTRICTION;
  }
  private isDeliveryAddressGpsCoordinatesEnabled(): boolean {
    const setting =
      this.selectedStore?.settings?.DELIVERY_ADDRESS_GPS_COORDINATES;
    if (typeof setting === 'string') {
      const normalizedSetting = setting.toUpperCase();
      return (
        normalizedSetting === 'TRUE' ||
        normalizedSetting === 'MANDATORY' ||
        normalizedSetting === 'OPTIONAL'
      );
    }
    return setting === true;
  }

  isNotDeliverableAddress(): boolean {
    if (!this.getZoneRestrictions()) {
      return false;
    }
    if (this.zoneStateData.status === 'NOTDELIVERABLE_ADDRESS') {
      return true;
    }
    if (this.zoneStateData.status === 'NOTDELIVERABLE_POSTCODE') {
      return this.isDeliveryAddressGpsCoordinatesEnabled();
    }
    return false;
  }

  isNotDeliverablePostCode(): boolean {
    return (
      this.getZoneRestrictions() &&
      this.zoneStateData.status === 'NOTDELIVERABLE_POSTCODE' &&
      !this.isDeliveryAddressGpsCoordinatesEnabled()
    );
  }

  isDeliverable(): boolean {
    return (
      this.getZoneRestrictions() && this.zoneStateData.status === 'DELIVERABLE'
    );
  }

  isGeolocationRequired(): boolean {
    if (!this.getZoneRestrictions()) {
      return false;
    }
    // A driving-distance zone is evaluated from coordinates, so a Pickup &
    // Drop-off store that has one needs both legs geocoded — otherwise a
    // manually typed address is never resolved and the fee stays at 0.00.
    // Gated on hasDrivingDistanceZone (surfaced by the backend): a P&D store
    // whose zones are postcode-based must not be forced through geocoding, or a
    // geocode failure would falsely mark it NOTDELIVERABLE and block Submit.
    if (
      isPickupAndDropoffFulfilmentMethod(
        this.selectedStore?.settings?.ORDER_FULFILMENT_METHOD,
      ) &&
      this.selectedStore?.hasDrivingDistanceZone
    ) {
      return true;
    }
    return (
      this.selectedStore.settings.DELIVERY_ADDRESS_GPS_COORDINATES ===
        'MANDATORY' &&
      (this.ifOnlyDeliveryToAddress() ||
        (this.ifEnabledDeliveryAtAddress() &&
          this.getPickupMethodAsInt() === PICKUP_METHOD.AT_ADDRESS))
    );
  }

  getOrderMinAmountDelivery() {
    if (
      this.zoneStateData?.status === 'DELIVERABLE' &&
      this.zoneStateData.settings?.orderMinAmountDelivery
    ) {
      return this.zoneStateData.settings.orderMinAmountDelivery;
    }
    return this.selectedStore.orderMinAmountDelivery;
  }

  /**
   * The minimum order amount for ordering at a location (table). The backend resolves the
   * table -> section -> store priority chain and returns the effective value on the validated
   * location, so we never re-implement that chain here. Falls back to the store-level setting
   * when no location has been validated yet. Zero (or blank) means "no location minimum".
   */
  getOrderMinAmountLocation(): number {
    const resolved = this.validLocations?.minOrderAmount;
    if (resolved !== null && resolved !== undefined) {
      return Number(resolved);
    }
    return Number(this.selectedStore?.settings?.ORDER_MIN_AMOUNT_LOCATION ?? 0);
  }

  /**
   * The minimum that actually applies to an order placed at a location. Mirrors the backend
   * precedence: the location minimum wins when it is configured; otherwise the delivery minimum
   * applies, but only for addressed locations (rooms / addressed locations where a delivery fee
   * applies) exactly as before this feature existed.
   */
  getEffectiveLocationMinimum(): number {
    const locationMinimum = this.getOrderMinAmountLocation();
    if (locationMinimum > 0) {
      return locationMinimum;
    }
    return this.locationDeliveryFeeApply()
      ? Number(this.getOrderMinAmountDelivery())
      : 0;
  }

  /**
   * True when the minimum in effect for a location order comes from the location chain (table /
   * section / store) rather than from the delivery-minimum fallback. Drives the message wording:
   * a location-chain minimum reads "...is required" (no "for delivery"), while the delivery
   * fallback keeps the existing "...for delivery" wording because it is literally the delivery
   * minimum.
   */
  isLocationChainMinimumInEffect(): boolean {
    return this.getOrderMinAmountLocation() > 0;
  }

  /** Translation key for the minimum-not-met message shown for a location order. */
  getLocationMinimumMessageKey(): string {
    return this.isLocationChainMinimumInEffect()
      ? 'public.checkout.minimumAmountLocation'
      : 'public.checkout.minimumAmount';
  }

  /** Formatted effective location minimum, for the location minimum-not-met message. */
  getFormattedLocationMinAmount(): string {
    if (this.selectedStore) {
      return this.formatPricePipe.transform(
        this.getEffectiveLocationMinimum(),
        this.selectedStoreLocale,
        this.selectedStoreCurrency,
        this.selectedStoreCurrencySymbol,
      );
    }
    return '';
  }

  /**
   * Message key for the summary line, which reflects the currently selected pickup method:
   * the location wording when ordering at a location, the delivery wording otherwise.
   */
  getActiveMinimumMessageKey(): string {
    return this.getPickupMethodAsInt() === PICKUP_METHOD.AT_LOCATION
      ? this.getLocationMinimumMessageKey()
      : 'public.checkout.minimumAmount';
  }

  /** Formatted minimum for the summary line, matching the currently selected pickup method. */
  getActiveFormattedMinAmount(): string {
    return this.getPickupMethodAsInt() === PICKUP_METHOD.AT_LOCATION
      ? this.getFormattedLocationMinAmount()
      : this.getFormattedMinAmount();
  }

  getOrderFreeDelivery() {
    if (
      this.zoneStateData?.status === 'DELIVERABLE' &&
      this.zoneStateData.settings.orderAmountFreeDelivery
    ) {
      return this.zoneStateData.settings.orderAmountFreeDelivery;
    }
    return this.selectedStore.orderAmountFreeDelivery;
  }

  getOrderFeeDelivery() {
    return this.cartData.deliveryFee;
  }
  shouldShowDeliveryZoneNameNextToFee(): boolean {
    return this.showDeliveryZoneNameNextToFee;
  }

  getDeliveryFeeStoreZoneName(): string {
    return this.deliveryFeeStoreZoneName;
  }

  shouldShowAddressDeliveryFeeHint(): boolean {
    return this.showAddressDeliveryFeeHint;
  }

  shouldShowDeliveryFeeRow(): boolean {
    return this.showDeliveryFeeRow;
  }

  private updateDeliveryFeeDisplayState(): void {
    // Suppress zone-name display when P&D addresses are incomplete, but keep
    // the fee row visible so the user sees ₹0.00 (or the last known fee) while
    // editing. Previously this returned INITIAL_DELIVERY_FEE_DISPLAY_STATE
    // (showDeliveryFeeRow = false) which caused the fee to disappear entirely
    // during address changes and on initial page load (#9992).
    const hideZoneFeedback = this.shouldHidePickupAndDropoffZoneFeedback();
    const storeZoneName = hideZoneFeedback
      ? ''
      : this.cartData?.storeZoneName?.trim() || '';

    const orderFeeDelivery = this.cartData?.deliveryFee;
    const showDeliveryZoneNameNextToFee =
      !hideZoneFeedback &&
      !!this.cartData?.showDeliveryZoneNameNextToFee &&
      !!storeZoneName;
    const showDeliveryFeeValueOrZoneName = orderFeeDelivery != null;
    const pickupMethod = Number.parseInt(
      `${this.orderMetaData?.data?.deliveryMethod ?? ''}`,
      10,
    );
    const showAddressDeliveryFeeHint =
      !hideZoneFeedback &&
      pickupMethod === PICKUP_METHOD.AT_ADDRESS &&
      showDeliveryFeeValueOrZoneName;
    const showDeliveryFeeRow =
      showDeliveryFeeValueOrZoneName &&
      (this.ifOnlyDeliveryToAddress() || !!this.cartData?.deliveryFeeApply);
    const nextDeliveryFeeDisplayState: DeliveryFeeDisplayState = {
      showDeliveryZoneNameNextToFee,
      deliveryFeeStoreZoneName: storeZoneName,
      showAddressDeliveryFeeHint,
      showDeliveryFeeRow,
    };

    this.setDeliveryFeeDisplayState(nextDeliveryFeeDisplayState);
  }

  private shouldHidePickupAndDropoffZoneFeedback(): boolean {
    if (
      !isPickupAndDropoffFulfilmentMethod(
        this.selectedStore?.settings?.ORDER_FULFILMENT_METHOD,
      )
    ) {
      return false;
    }

    const orderMeta = this.orderMetaData?.data;
    return !hasCompletePickupAndDropoffAddressState({
      pickupStreet: orderMeta?.pickupStreet,
      customerStreet: orderMeta?.customerStreet,
      pickupLatitude: orderMeta?.pickupLatitude,
      pickupLongitude: orderMeta?.pickupLongitude,
      latitude: orderMeta?.latitude,
      longitude: orderMeta?.longitude,
    });
  }

  private setDeliveryFeeDisplayState(
    nextDeliveryFeeDisplayState: DeliveryFeeDisplayState,
  ): void {
    this.showDeliveryZoneNameNextToFee =
      nextDeliveryFeeDisplayState.showDeliveryZoneNameNextToFee;
    this.deliveryFeeStoreZoneName =
      nextDeliveryFeeDisplayState.deliveryFeeStoreZoneName;
    this.showAddressDeliveryFeeHint =
      nextDeliveryFeeDisplayState.showAddressDeliveryFeeHint;
    this.showDeliveryFeeRow = nextDeliveryFeeDisplayState.showDeliveryFeeRow;
    this.deliveryFeeDisplayStateSubject.next(nextDeliveryFeeDisplayState);
  }

  getOrderServiceCharge() {
    return this.cartData.serviceChargeAmount;
  }

  getOrderTipValue() {
    return this.cartData.tip;
  }

  /**
   * get the UUID for a store promotion rule added to the cart
   * @param offerId string - id for the offer we want to check if it is a part of store promotion rule
   */
  getOfferRuleUuid(offerId: number, categoryId?: number): string {
    return (
      this.cartItems?.find(
        (item) =>
          item.offerId === offerId &&
          this.matchesOrderingRuleGroup(item, categoryId),
      )?.uuid || ''
    );
  }

  getOfferRuleQuantity(offerId: number, categoryId: number): number {
    return (this.cartItems || [])
      .filter(
        (item) =>
          item.offerId === offerId &&
          this.matchesOrderingRuleGroup(item, categoryId),
      )
      .reduce((sum, item) => sum + (item.quantity || 1), 0);
  }

  private isOrderingRuleItem(item: OrderItem): boolean {
    return (
      item.hierarchyLevel === 'RULE' ||
      item.hierarchyLevel === 'RULE_DELIVERY_FEE'
    );
  }

  private matchesOrderingRuleGroup(
    item: OrderItem,
    categoryId?: number,
  ): boolean {
    if (!this.isOrderingRuleItem(item)) return false;
    if (categoryId === undefined) return true;
    if (item.categoryId != null) {
      return item.categoryId === categoryId;
    }
    // Older backend responses may omit categoryId. Their rule-local options belong to
    // one group, even when several rules reference it. Do not guess across different groups.
    const groups = new Set<number>();
    (this.storeRules || []).forEach((rule) => {
      (rule.actions || []).forEach((action) => {
        if (
          action.data?.offers?.some((offer) => offer.offerId === item.offerId)
        ) {
          groups.add(action.data.categoryId);
        }
      });
    });
    return groups.size === 1 && groups.has(categoryId);
  }

  isRuleValid(ruleIndex) {
    if (this.applicableStoreRules[ruleIndex]) {
      let allConditionsAreTrue = true;
      this.applicableStoreRules[ruleIndex].conditions.forEach((condition) => {
        switch (condition.type) {
          case 'ORDER_AMOUNT_RANGE':
            if (
              this.getCartTotal(true) < condition.data.min ||
              this.getCartTotal(true) > condition.data.max
            ) {
              allConditionsAreTrue = false;
            }
            break;
          case 'DELIVERY_MODE':
            if (this.getPickupMethodStr() !== condition.data.deliveryMode) {
              allConditionsAreTrue = false;
            }
            break;
          default:
            // do nothing
            break;
        }
      });
      if (allConditionsAreTrue) {
        // the condition is still valid
        return true;
      }
    }
    // the condition is NO LONGER valid. DO NOT ADD TO CART!
    return false;
  }

  hasStoreRuleOffers() {
    if (
      !this.checkoutStateData.storeRuleFormValid ||
      !this.checkoutStateData.feeRuleFormValid
    ) {
      // I should not be able to get to this point if the storeRuleForm is invalid
      this.store.dispatch(
        new ErrorMessage('public.global.errorExpected', '501'),
      );
    }
    if (!this.checkoutStateData.storeRuleOffers) {
      return false;
    }
    // update store offer rules - as some rules may have expired
    const validStoreRulesAtCheckout = [];
    this.checkoutStateData.storeRuleOffers.forEach((offer) => {
      if (
        this.storeRules[offer.ruleIndex] &&
        this.isRuleValid(offer.ruleIndex)
      ) {
        validStoreRulesAtCheckout.push(offer);
      }
    });
    this.store.dispatch(
      new AddCheckoutState('storeRuleOffers', validStoreRulesAtCheckout),
    );
    if (validStoreRulesAtCheckout.length === 0) {
      return false;
    }
    return true;
  }

  /**
   * check if the offer is part of a store promotion rule
   * @param offerId string - id for the offer we want to check if it is a part of store promotion rule
   */
  ifOfferRule(offerId: number) {
    let ret = false;
    this.storeRules.map((rule) => {
      rule.actions.map((action) => {
        if (action.data.offers) {
          action.data.offers.map((offer) => {
            if (offer.offerId === offerId) {
              ret = true;
            }
          });
        }
      });
    });
    return ret;
  }

  /**
   * check if the offer is added to cart
   * usually used to determine if a store promotion
   * rule offer is already added to cart
   * @param offerId string - id for the offer we want to check if it is a part of cart items
   */
  ifCartOfferRule(offerId: number, categoryId?: number): boolean {
    return (
      this.cartItems?.some(
        (item) =>
          item.offerId === offerId &&
          this.matchesOrderingRuleGroup(item, categoryId),
      ) || false
    );
  }

  /**
   * check if the offer is added to cart
   * usually used to determine if a store promotion
   * rule offer is already added to cart
   * @param offerId string - id for the offer we want to check if it is a part of cart items
   */
  ifCartOfferRuleMinZeroMaxOne(offerId: number) {
    let ret = false;
    const rules = this.getApplicableStoreRules();
    rules.map((rule) => {
      rule.actions.map((action) => {
        if (
          action.data &&
          action.data.min !== undefined &&
          action.data.min === 0 &&
          action.data.max !== undefined &&
          action.data.max === 1
        ) {
          action.data.offers.map((offer) => {
            if (offer.offerId === offerId) {
              ret = true;
            }
          });
        }
      });
    });

    return ret;
  }

  /**
   * for now we are depercating this mechanism of adding offer rules on submission
   * we will add/persist them on select directly to the order/be
   */
  submitStoreRuleOffers() {
    if (
      !this.checkoutStateData.storeRuleFormValid ||
      !this.checkoutStateData.feeRuleFormValid
    ) {
      // I should not be able to get to this point if the storeRuleForm is invalid
      this.store.dispatch(
        new ErrorMessage('public.global.errorExpected', '501'),
      );
    }
    if (!this.checkoutStateData.storeRuleOffers) {
      return;
    }
    const payload = [];
    this.checkoutStateData.storeRuleOffers.forEach((offer) => {
      if (
        this.storeRules[offer.ruleIndex] &&
        this.isRuleValid(offer.ruleIndex)
      ) {
        const offerItem = {
          offerId: offer.offerId,
          quantity: offer.quantity,
          comment: '',
        };
        payload.push(offerItem);
        // at the moment it is assumed that only one order item would be added to cart
        // if that isn't the case this will present an issue
        this.store.dispatch(
          new AddRuleOrderItem(
            this.selectedStore.id,
            this.orderUuid,
            offerItem,
          ),
        );
      }
    });
  }

  ifSubmitOrderReady(isAdminOrderUpdate?: boolean) {
    // true if: invalid payment method is selected or checkout options form is invalid
    if (this.OrderBtnDisabled) {
      this.logger('Order button disabled');
      return false;
    }
    // true if: store rules are enabled and there is a required selection which has not been made
    if (
      !this.checkoutStateData.storeRuleFormValid ||
      !this.checkoutStateData.feeRuleFormValid
    ) {
      this.logger('Invalid rules');
      return false;
    }

    if (
      (this.showEditableLocation() || this.showReadOnlyLocation()) &&
      !this.checkoutStateData.checkoutOptionsFormValid
    ) {
      this.logger('Invalid location');
      return false;
    }

    // true if: minimum order amount is met and delivery at address is enabled/available
    if (!this.ifMinimumOrderAmountMet()) {
      this.logger('Minimum order amount not met');
      return false;
    }

    // true if: valid wishtime is provided.
    // a valid wishtime is considered:
    // no wish time selection at all,
    // I want to receive ASAP, or
    // valid timepicker value provided when want to specify it
    if (
      !isAdminOrderUpdate &&
      !this.isCheckoutMetaData('timeSelectionValid') &&
      !this.getCheckoutMetaDataValue('timeSelectionValid')
    ) {
      this.logger('Invalid time selection');
      return false;
    }

    if (
      !isAdminOrderUpdate &&
      this.getPickupMethodAsInt() === PICKUP_METHOD.AT_ADDRESS &&
      this.getZoneRestrictions() &&
      !this.isDeliverable()
    ) {
      this.logger('Invalid zone');
      return false;
    }

    if (
      !isAdminOrderUpdate &&
      (this.ifOnlyInStore() || this.getPickupMethodAsInt() === 0) &&
      !this.isValidLocation()
    ) {
      this.logger('Invalid location');
      return false;
    }

    if (!this.checkoutStateData.personalFormValid) {
      this.logger('Invalid personal form');
      return false;
    }

    if (
      this.isBillingAddressRequired() &&
      !this.checkoutStateData.billingInfoFormValid
    ) {
      this.logger('Invalid billing form');
      return false;
    }

    if (
      this.isBuzzerRequired() &&
      !this.checkoutStateData.buzzerInfoFormValid
    ) {
      this.logger('Invalid buzzer form');
      return false;
    }

    // #10284 A voucher that fully covers the order drops the live total to ≤ 0. The
    // online payment component is already hidden for such orders (isPaymentMethodOnline()
    // guards on getCartTotal() > 0) and parseMetaData() submits them as free orders
    // (status SUBMITTED), so the payment-method / pay-later gates below must not block
    // Submit — otherwise the button is silently unresponsive. personalFormValid is still
    // enforced independently above, so skipping the combined check here is safe.
    const isZeroTotalOrder = this.getCartTotal() <= 0;

    if (
      !isZeroTotalOrder &&
      !(
        (isAdminOrderUpdate ||
          !this.isPaymentComponent() ||
          (this.isPaymentComponent() && this.isPaymentValid())) &&
        this.checkoutStateData.personalFormValid
      )
    ) {
      this.logger('Invalid payment');
      return false;
    }

    if (this.isPaylaterInvalid()) {
      this.logger('Invalid pay later option');
      return false;
    }

    // #9947 Admin order capture: NOW_VOUCHER requires a VALID voucher outcome before Submit.
    // isAdminVoucherInvalid() is self-gating — it is false unless paymentCategory === 'NOW_VOUCHER',
    // which is only ever set on the admin-capture (/capture/) path — so it stands alone. The prior
    // `isAdminOrderUpdate` guard was the /orders/ flag (isAdminOrderUpdate() matches '/orders/', not
    // '/capture/'), which silently disabled this gate for the only flow that produces NOW_VOUCHER.
    if (this.isAdminVoucherInvalid()) {
      this.logger('Invalid admin voucher (NOW_VOUCHER without VALID outcome)');
      return false;
    }

    return true;
  }

  /**
   * #9947 True iff admin selected NOW_VOUCHER but the voucher capture outcome is not VALID.
   * Used as a Submit gate on the admin-capture path.
   */
  public isAdminVoucherInvalid(): boolean {
    const category = this.orderMetaData.data.paymentCategory;
    if (category !== 'NOW_VOUCHER') return false;
    const outcome = this.orderMetaData.data.adminVoucherOutcome;
    return outcome !== 'VALID';
  }

  public isPaylaterInvalid(): boolean {
    // #10284 When a voucher fully covers the order (live total ≤ 0) there is nothing
    // left to pay, so a pay-later selection is not required: it must neither block
    // Submit nor surface the "must select one" error. getCartTotal() is voucher-aware
    // (it subtracts voucherDiscount), unlike the cartData.totalDiscountedPrice that
    // isPaymentComponent() reads — which is why a €0.00 voucher order was still gated.
    if (this.getCartTotal() <= 0) {
      return false;
    }
    return (
      (this.orderMetaData.data.paymentOption == 0 ||
        this.orderMetaData.data.paymentOption == -1) &&
      [
        'CASH',
        'CARD',
        'CASH_CARD',
        'CASH_BANK_TRANSFER',
        'CASH_CHEQUE',
      ].includes(this.selectedStore.settings.PAY_LATER_OPTION) &&
      !this.locationService.isAdminOrderCaptureUpdate() &&
      !this.orderMetaData.data.preferredMode
    );
  }

  isPaymentComponent() {
    return (
      ((this.ifEnabledPaymentSelection() || this.ifEnabledPaymentMandatory()) &&
        this.cartData.totalDiscountedPrice > 0) ||
      this.ifInactivePaymentSelection()
    );
  }

  isPaymentValid() {
    // Check if payment is not enabled, return true
    if (!this.ifEnabledPayment()) {
      return true;
    }

    // include only valid payment methods
    const validPaymentMethods = ['', ...Object.values(PaymentMethod)];

    // Checks if payment option is enabled and payment method is mandatory or optional,
    if (this.ifEnabledPaymentSelection() || this.ifEnabledPaymentMandatory()) {
      const paymentOption = this.getOrderMetaData('paymentOption');
      return (
        paymentOption === 0 ||
        paymentOption === '' ||
        (paymentOption > 0 &&
          (this.selectedStore.activePaymentMethods || []).some(
            (paymentMethod) => validPaymentMethods.includes(paymentMethod),
          ))
      );
    }

    // If none of the above conditions are met, return false
    return false;
  }

  isTimeShowDisabled() {
    if (!this.selectedStore || !this.selectedStore.settings) {
      return false;
    }
    switch (this.getPickupMethodStr()) {
      case 'IN_STORE_LOCATION':
        return this.selectedStore.settings
          .DELIVERY_IN_STORE_LOCATION_HIDE_FUTURE_WISH_DATE;
      case 'NO_LOCATION':
        return this.selectedStore.settings
          .DELIVERY_NO_LOCATION_HIDE_FUTURE_WISH_DATE;
      case 'ADDRESS':
        return this.selectedStore.settings
          .DELIVERY_ADDRESS_HIDE_FUTURE_WISH_DATE;
    }
    return false;
  }

  isFutureOrderingEnabled() {
    const settings = this.selectedStore.settings;

    return (
      (settings.DELIVERY_IN_STORE_LOCATION &&
        settings.DELIVERY_IN_STORE_LOCATION_CHOOSE_FUTURE_ORDER_DATE) ||
      (settings.DELIVERY_NO_LOCATION &&
        settings.DELIVERY_NO_LOCATION_CHOOSE_FUTURE_ORDER_DATE) ||
      (settings.DELIVERY_ADDRESS &&
        settings.DELIVERY_ADDRESS_CHOOSE_FUTURE_ORDER_DATE)
    );
  }

  isShowParentItemPrice(orderItem: OrderItem) {
    if (
      orderItem.quantity > 1 ||
      (orderItem.discountValue !== undefined && orderItem.discountValue !== 0)
    ) {
      return true;
    }
    if (
      orderItem.childOrderItems !== undefined &&
      orderItem.childOrderItems.length > 0
    ) {
      let found = false;
      orderItem.childOrderItems.forEach((childOrderItem) => {
        if (
          childOrderItem.offerPrice !== undefined &&
          childOrderItem.offerPrice !== 0
        ) {
          found = true;
        }
      });
      return found;
    }
    return false;
  }

  invalidSubmit() {
    return this.checkoutStatus === 'INVALID_SUBMIT';
  }

  // TODO Review this
  showErrors(control: AbstractControl) {
    return this.invalidSubmit() && control?.errors;
  }

  isInvalid(control: AbstractControl) {
    return control?.invalid && control?.dirty && this.invalidSubmit();
  }

  logger(message: string) {
    if (!environment.production) {
      // Logged at `error` level (not `console.log`) so the line is visible under
      // Chrome's default Console filter (Errors / Warnings). The Customer UI's
      // `ifSubmitOrderReady` rejection path otherwise hides itself from testers
      // — see card #9632. Reason strings remain byte-identical so the team's
      // `git blame` on the message text continues to map cleanly to source.
      console.error('Checkout Service: ', message);
    }
  }

  /**
   * True when the store captures no customer details for pickup — i.e. the
   * name, email and phone pickup fields are all Hidden. Shared by
   * allFieldsHiddenForPickup() and hidePickupInformationSection().
   */
  private allPickupCaptureFieldsHidden() {
    return (
      this.selectedStore?.settings?.PICKUP_NAME_SELECTION ===
        OrderingSetting.HIDDEN &&
      this.selectedStore?.settings?.PICKUP_EMAIL_SELECTION ===
        OrderingSetting.HIDDEN &&
      this.selectedStore?.settings?.PICKUP_TEL_SELECTION ===
        OrderingSetting.HIDDEN
    );
  }

  allFieldsHiddenForPickup() {
    return (
      !this.customerAuthService.enableCustomerAuth() &&
      this.isShowOnlySelfPickup() &&
      this.allPickupCaptureFieldsHidden()
    );
  }

  /**
   * Whether the entire "Your Information" section must be hidden on the
   * self-pickup checkout because the store captures no customer details.
   *
   * True when this is a pickup-only flow and all three pickup capture
   * fields (name, email, phone) are Hidden — independently of whether
   * customer login is left Optional. The Optional / Off login cases are
   * hidden as requested; the Mandatory case is deliberately excluded via
   * customerAuthRequired() because mandatory authentication is enforced
   * before checkout (the customer reaches checkout already logged in), so
   * there is no login prompt to hide there. In admin order capture
   * customerAuthRequired() is false, so the section (and its "+ Select
   * Customer" button) is hidden there too.
   */
  hidePickupInformationSection() {
    return (
      this.isShowOnlySelfPickup() &&
      this.allPickupCaptureFieldsHidden() &&
      !this.customerAuthService.customerAuthRequired()
    );
  }

  isBillingAddressRequired(): boolean {
    return this.selectedStore?.settings?.ENABLE_BILLING_ADDRESS == 'MANDATORY';
  }

  isBuzzerRequired(): boolean {
    return this.selectedStore?.settings?.ENABLE_BUZZER_NUMBER == 'MANDATORY';
  }

  handleInvalidLocation(selectedStoreAlias: string) {
    // Card #11603 - through LocationService, so the visit's copy of the cookie
    // (kept when the browser refuses cookies) goes too.
    this.locationService.removeLocationOrder(selectedStoreAlias);
    this.store.dispatch(new ClearStoreLocation());
    this.store.dispatch(new UpdateLocation(null, this.helper.getDeviceID()));
    this.router.navigateByUrl('/');
  }

  proceedToCheckout() {
    this.store.dispatch(new ProceedToCheckout());
    const loginRequired =
      !this.customerAuthService.isUserLoggedIn() &&
      this.customerAuthService.customerAuthRequired();
    const requiresPreviousOrder =
      !!this.selectedStore?.settings?.REQUIRE_PREVIOUS_ORDER_NUMBER;

    if (loginRequired && requiresPreviousOrder) {
      this.router.navigateByUrl(
        `${this.locationService.base_url('login')}?next=precheckout`,
      );
      return;
    }

    // Check if single item per order is enabled for different navigation logic
    const singleItemPerOrder =
      this.selectedStore?.settings?.SINGLE_PARENT_ITEM_PER_ORDER === 'true';

    const cartDestination = singleItemPerOrder ? 'checkout' : 'cart';
    const destination = loginRequired
      ? 'login'
      : requiresPreviousOrder
        ? 'precheckout'
        : cartDestination;

    const targetUrl = this.locationService.base_url(destination);

    // Recommendation hook (#9303): only inject the recommendation step between cart and
    // checkout when there is a basket-bearing destination (cart / checkout). Login and
    // precheckout are auth gates that come before basket display — recommendations belong
    // after them, so the customer hits the cart first and the next proceed-to-checkout
    // re-enters this method with the auth gate cleared. The coordinator either navigates
    // directly to the fallback (no rules, master toggle off, or fetch error) or pushes the
    // first recommendation page with the rest of the queue + the original destination.
    if (
      this.selectedStore?.id &&
      (destination === 'cart' || destination === 'checkout')
    ) {
      this.recommendationsFlowCoordinator.startFlowOrNavigate({
        storeId: this.selectedStore.id,
        destination,
        languageId: this.selectedStore.language?.id,
      });
      return;
    }

    this.router.navigateByUrl(targetUrl);
  }

  /**
   * Returns true if ORDER_FULFILMENT_METHOD is 'PICKUP_AND_RETURN'.
   * Optionally accepts a store object to check against.
   */
  isPickupAndReturn(): boolean {
    return (
      this.selectedStore?.settings?.ORDER_FULFILMENT_METHOD ===
      'PICKUP_AND_RETURN'
    );
  }

  isPickupAndReturnMultiStore(): boolean {
    return this.getAllChildStores().some(
      (childStore) =>
        childStore.settings?.ORDER_FULFILMENT_METHOD === 'PICKUP_AND_RETURN',
    );
  }

  /**
   * The descendants the parent still sells from — Independent branches
   * excluded (#11576). An Independent branch must not be able to make a
   * delivery method (or the method step itself) appear on the parent's chooser
   * just by existing.
   *
   * The exclusion changed the meaning of this accessor for every caller, so the
   * full set was swept rather than the three the change was aimed at. As of
   * #11576 it is exactly three call sites, all in this file, none in a
   * template, and all three ask the same question — *does any store in the
   * group support X* — for which excluding an opted-out branch is the correct
   * answer:
   *
   * - `ifEnabledSelfPickupMultiStore()` — does the group offer pickup at all.
   * - `ifEnabledDeliveryAtAddressMultiStore()` — does it offer delivery to an
   *   address. These two decide whether the delivery-method step renders.
   * - `isPickupAndReturnMultiStore()` — does the group run pickup-and-return.
   *
   * No caller uses this list for basket, pricing or ordering context, which is
   * the case where a silently shorter list would be a behaviour change the
   * story never asked for. Keep that true: a new caller that needs *every*
   * descendant, opted-out ones included, must read
   * `relation.allDescendantStores` directly and say why, not widen this.
   */
  getAllChildStores(): StoreRelations[] {
    return StoreUtils.getLinkedDescendantStores(this.selectedStore);
  }

  isServiceChargePercentageBased(): boolean {
    return this.cartData.serviceChargeCalculationMethod === 'PERCENTAGE';
  }

  getDeliveryServiceChargePercentage(): number {
    return (
      this.selectedStore?.settings?.SERVICE_CHARGE_DELIVERY_PERCENTAGE || 0
    );
  }
}
